import { Button, Card, CardButtonActionMode, HeaderGradient, MiniCard, Row, StatusDot } from '@app/components';
import React, { Fragment, memo } from 'react';
import { theme } from '@app/theme';
import { useDynamicSizes } from '@app/hooks';
import LinearGradient from 'react-native-linear-gradient';
import { StyleSheet } from 'react-native';
import { IService, OrderMode, OrderChannel, getOrderModeFR } from '@app/common';
import { DateTime } from 'luxon';
import { triptychSelectors } from '@app/store';
import { useAppSelector } from '@app/store/hooks';
import { convertToEuros } from "@app/common";
import styled from '@2pulse/responsive';
import { normalize } from '@app/helpers';
import { SlantTitle } from '@app/components/typography';
import SvgStats from '@app/components/icons/SvgStats';
import * as R from 'remeda';

type ServiceCardProps = {
  service?: Partial<IService>;
  toggle: (mode: CardButtonActionMode) => void;
};

const getChannelLabel = (channel: OrderChannel) => {
  switch (channel) {
    case OrderChannel.WEBSITE: return 'Web';
    case OrderChannel.TICKEAT_OS: return 'Caisse';
    case OrderChannel.UBER_EATS: return 'Uber Eats';
    case OrderChannel.JUST_EAT: return 'Just Eat';
    case OrderChannel.DELIVEROO: return 'Deliveroo';
    case OrderChannel.DELICITY: return 'Delicity';
    case OrderChannel.KIOSK: return 'Borne';
    case OrderChannel.TABLE: return 'Table';
    default: return String(channel);
  }
}

const getModeColor = (mode: OrderMode) => {
  switch (mode) {
    case OrderMode.DELIVERY: return theme.colors.celadonblue;
    case OrderMode.TAKEAWAY: return theme.colors.gold;
    case OrderMode.ONSPOT: return '#9D4EDD'; // Violet vif
    default: return '#FF6B9D'; // Rose vif
  }
}

const getChannelColor = (channel: OrderChannel) => {
  switch (channel) {
    case OrderChannel.WEBSITE: return '#4D9FEB'; // Bleu vif
    case OrderChannel.TICKEAT_OS: return '#7C3AED'; // Violet vif
    case OrderChannel.UBER_EATS: return '#06C167'; // Vert Uber (déjà vif)
    case OrderChannel.JUST_EAT: return '#FF5A00'; // Orange Just Eat (déjà vif)
    case OrderChannel.DELIVEROO: return '#00CCBC'; // Turquoise Deliveroo (déjà vif)
    case OrderChannel.DELICITY: return '#0066B3'; // Bleu Delicity
    case OrderChannel.KIOSK: return '#8D269C'; // Violet borne
    case OrderChannel.TABLE: return '#0EA5E9'; // Cyan Table
    default: return '#FF6B9D'; // Rose vif
  }
}

export const ServiceCard = memo(({ service, toggle }: ServiceCardProps) => {
  const { subContainerHeight } = useDynamicSizes();

  const isOpen = useAppSelector(triptychSelectors.selectIsOpen);

  const totalOrders = service?.orders?.length ?? 0;
  const cancelledOrders = service?.orders?.filter((o) => o.status === 'cancelled')?.length ?? 0;
  const validOrders = totalOrders - cancelledOrders;
  const averageTicket = validOrders > 0 ? (service?.caTTC ?? 0) / validOrders : 0;

  const totalCaTTC = service?.caTTC ?? 1;

  return (
    <Card>

      <HeaderGradient>
        <LinearGradient
          start={{ x: 0.5, y: 0 }}
          end={{ x: 0.5, y: 1 }}
          locations={[0, 1]}
          colors={['rgba(253, 255, 252, 0)', 'rgba(253, 255, 252, 1)']}
          style={styles.headerGradient}
        />
      </HeaderGradient>

      <Card.TopHeader>
        <Card.TopHeaderLabel>Récapitulatif du service</Card.TopHeaderLabel>
      </Card.TopHeader>

      <Card.Container height={subContainerHeight}>
        <Card.Background source={require('@app/assets/img/cardbackground.png')} />

        <Card.Content>
          <Card.Header>
            <Row justify="space-between" align="center">
              <Card.HeaderTitles>
                <Card.Title numberOfLines={2}>Service #{service?.serviceNumber} - {service?.shop?.publicName}</Card.Title>
                {service?.fromDate && service?.toDate && (
                  <Card.Title small>Du {DateTime.fromISO(service?.fromDate ?? '').toFormat('dd/MM/yyyy')} au {DateTime.fromISO(service?.toDate ?? '').toFormat('dd/MM/yyyy')}</Card.Title>
                )}
                {service?._id && <Card.Subtitle>Ref: {service._id}</Card.Subtitle>}
              </Card.HeaderTitles>

              {!!service?._id && (
                <Button
                  title={isOpen ? "Fermer" : "Voir"}
                  bordered={isOpen}
                  onPress={() => toggle(isOpen ? "CLOSE" : "UPDATE")} />
              )}
            </Row>

            <Card.HeaderValues>
              <Card.HeaderValuesItem>
                <Card.Key numberOfLines={1}>CA TTC</Card.Key>
                <Card.Value>{convertToEuros(service?.caTTC ?? 0)} €</Card.Value>
              </Card.HeaderValuesItem>
              <Card.HeaderValuesItem>
                <Card.Key numberOfLines={1}>CA HT</Card.Key>
                <Card.Value>{convertToEuros(service?.caHT ?? 0)} €</Card.Value>
              </Card.HeaderValuesItem>
              <Card.HeaderValuesItem>
                <Card.Key numberOfLines={1}>TVA</Card.Key>
                <Card.Value>{convertToEuros(service?.caTVA ?? 0)} €</Card.Value>
              </Card.HeaderValuesItem>
            </Card.HeaderValues>
          </Card.Header>

        </Card.Content>

        {!R.isEmptyish(service) && (
          <MiniCard>
            <MiniCard.Tabs>
              <MiniCard.TabsHolder>
                <MiniCard.Tab Icon={SvgStats} isActive />
              </MiniCard.TabsHolder>
            </MiniCard.Tabs>
            <MiniCard.Container>
              <MiniCard.Content>

                <Card.LineBreak height={16} />

                <SlantTitle label="Statistiques" color={theme.colors.gold} />

                <Card.HelpScrollContainer extraStyle={{ paddingLeft: 60, paddingRight: 60, paddingTop: 60 }}>

                  {service?.orders && service.orders.length > 0 && (
                    <Fragment>
                      <Card.LineBreak height={24} />
                      <StatsGrid>
                        {totalOrders > 0 && (
                          <StatCard>
                            <LinearGradient
                              start={{ x: 0, y: 0 }}
                              end={{ x: 1, y: 1 }}
                              colors={['rgba(60, 172, 219, 0.2)', 'rgba(60, 172, 219, 0.05)']}
                              style={{
                                padding: normalize(20),
                                borderRadius: normalize(20),
                                borderWidth: 1,
                                borderColor: 'rgba(253, 255, 252, 0.1)',
                                flexDirection: 'row',
                                alignItems: 'center',
                                gap: normalize(16),
                              }}
                            >
                              <StatIconWrapper bgColor="rgba(60, 172, 219, 0.3)">
                                <StatIcon>📦</StatIcon>
                              </StatIconWrapper>
                              <StatContent>
                                <StatLabel>Commandes totales</StatLabel>
                                <StatValue>{totalOrders}</StatValue>
                                <StatSubtext>{validOrders} validées</StatSubtext>
                              </StatContent>
                            </LinearGradient>
                          </StatCard>
                        )}
                        {averageTicket > 0 && (
                          <StatCard>
                            <LinearGradient
                              start={{ x: 0, y: 0 }}
                              end={{ x: 1, y: 1 }}
                              colors={['rgba(242, 204, 143, 0.2)', 'rgba(242, 204, 143, 0.05)']}
                              style={{
                                padding: normalize(20),
                                borderRadius: normalize(20),
                                borderWidth: 1,
                                borderColor: 'rgba(253, 255, 252, 0.1)',
                                flexDirection: 'row',
                                alignItems: 'center',
                                gap: normalize(16),
                              }}
                            >
                              <StatIconWrapper bgColor="rgba(242, 204, 143, 0.3)">
                                <StatIcon>💰</StatIcon>
                              </StatIconWrapper>
                              <StatContent>
                                <StatLabel>Ticket moyen</StatLabel>
                                <StatValue>{convertToEuros(averageTicket).toFixed(2)}€</StatValue>
                                <StatSubtext>par commande</StatSubtext>
                              </StatContent>
                            </LinearGradient>
                          </StatCard>
                        )}
                        {cancelledOrders > 0 && (
                          <StatCard>
                            <LinearGradient
                              start={{ x: 0, y: 0 }}
                              end={{ x: 1, y: 1 }}
                              colors={['rgba(229, 89, 52, 0.2)', 'rgba(229, 89, 52, 0.05)']}
                              style={{
                                padding: normalize(20),
                                borderRadius: normalize(20),
                                borderWidth: 1,
                                borderColor: 'rgba(253, 255, 252, 0.1)',
                                flexDirection: 'row',
                                alignItems: 'center',
                                gap: normalize(16),
                              }}
                            >
                              <StatIconWrapper bgColor="rgba(229, 89, 52, 0.3)">
                                <StatIcon>❌</StatIcon>
                              </StatIconWrapper>
                              <StatContent>
                                <StatLabel>Commandes annulées</StatLabel>
                                <StatValue color={theme.colors.crimson}>{cancelledOrders}</StatValue>
                                <StatSubtext>{((cancelledOrders / totalOrders) * 100).toFixed(1)}% du total</StatSubtext>
                              </StatContent>
                            </LinearGradient>
                          </StatCard>
                        )}
                      </StatsGrid>
                      <Card.LineBreak height={32} />
                    </Fragment>
                  )}

                  {/* Graphiques par Mode */}
                  {service?.byOrderMode && service.byOrderMode.length > 0 && (
                    <ModernChartSection>
                      <SectionHeader>
                        <SectionTitleRow>
                          <SectionIcon>🛵</SectionIcon>
                          <SectionTitle>Modes de commande</SectionTitle>
                        </SectionTitleRow>
                        <SectionSubtitle>{convertToEuros(totalCaTTC)}€ total</SectionSubtitle>
                      </SectionHeader>
                      <ChartWrapper>
                        {service.byOrderMode.map((item, idx) => {
                          const percentage = ((item.caTTC / totalCaTTC) * 100).toFixed(1);
                          return (
                            <ModernChartRow key={idx}>
                              <ChartRowHeader>
                                <StatusDot color={getModeColor(item.mode)} />
                                <ModernChartLabel>{getOrderModeFR(item.mode)}</ModernChartLabel>
                              </ChartRowHeader>
                              <ChartRowBody>
                                <ModernBarContainer>
                                  <ModernBarBackground />
                                  <ModernBar
                                    width={(item.caTTC / totalCaTTC) * 100}
                                    color={getModeColor(item.mode)}
                                  >
                                    <LinearGradient
                                      start={{ x: 0, y: 0 }}
                                      end={{ x: 1, y: 0 }}
                                      colors={['rgba(255, 255, 255, 0.2)', 'transparent']}
                                      style={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        bottom: 0,
                                        borderRadius: normalize(16),
                                      }}
                                    />
                                  </ModernBar>
                                </ModernBarContainer>
                                <ChartRowMetrics>
                                  <MetricValue>{convertToEuros(item.caTTC)}€</MetricValue>
                                  <MetricPercentage>{percentage}%</MetricPercentage>
                                  <MetricCount>{item.quantity} cmd</MetricCount>
                                </ChartRowMetrics>
                              </ChartRowBody>
                            </ModernChartRow>
                          );
                        })}
                      </ChartWrapper>
                    </ModernChartSection>
                  )}

                  {/* Graphiques par Canal */}
                  {service?.byOrderChannel && service.byOrderChannel.length > 0 && (
                    <ModernChartSection>
                      <SectionHeader>
                        <SectionTitleRow>
                          <SectionIcon>📱</SectionIcon>
                          <SectionTitle>Canaux de vente</SectionTitle>
                        </SectionTitleRow>
                        <SectionSubtitle>{service.byOrderChannel.length} canal{service.byOrderChannel.length > 1 ? 'aux' : ''} actif{service.byOrderChannel.length > 1 ? 's' : ''}</SectionSubtitle>
                      </SectionHeader>
                      <ChartWrapper>
                        {service.byOrderChannel.map((item, idx) => {
                          const percentage = ((item.caTTC / totalCaTTC) * 100).toFixed(1);
                          return (
                            <ModernChartRow key={idx}>
                              <ChartRowHeader>
                                <StatusDot color={getChannelColor(item.channel)} />
                                <ModernChartLabel>{getChannelLabel(item.channel)}</ModernChartLabel>
                              </ChartRowHeader>
                              <ChartRowBody>
                                <ModernBarContainer>
                                  <ModernBarBackground />
                                  <ModernBar
                                    width={(item.caTTC / totalCaTTC) * 100}
                                    color={getChannelColor(item.channel)}
                                  >
                                    <LinearGradient
                                      start={{ x: 0, y: 0 }}
                                      end={{ x: 1, y: 0 }}
                                      colors={['rgba(255, 255, 255, 0.2)', 'transparent']}
                                      style={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        bottom: 0,
                                        borderRadius: normalize(16),
                                      }}
                                    />
                                  </ModernBar>
                                </ModernBarContainer>
                                <ChartRowMetrics>
                                  <MetricValue>{convertToEuros(item.caTTC)}€</MetricValue>
                                  <MetricPercentage>{percentage}%</MetricPercentage>
                                  <MetricCount>{item.quantity} cmd</MetricCount>
                                </ChartRowMetrics>
                              </ChartRowBody>
                            </ModernChartRow>
                          );
                        })}
                      </ChartWrapper>
                    </ModernChartSection>
                  )}

                  {/* Graphiques par Méthode de paiement */}
                  {service?.byPaymentMethod && service.byPaymentMethod.length > 0 && (
                    <ModernChartSection>
                      <SectionHeader>
                        <SectionTitleRow>
                          <SectionIcon>💳</SectionIcon>
                          <SectionTitle>Méthodes de paiement</SectionTitle>
                        </SectionTitleRow>
                        <SectionSubtitle>{service.byPaymentMethod.length} méthode{service.byPaymentMethod.length > 1 ? 's' : ''}</SectionSubtitle>
                      </SectionHeader>
                      <ChartWrapper>
                        {service.byPaymentMethod.map((item, idx) => {
                          const percentage = ((item.total / totalCaTTC) * 100).toFixed(1);
                          return (
                            <ModernChartRow key={idx}>
                              <ChartRowHeader>
                                <StatusDot color={theme.colors.celadonblue} />
                                <ModernChartLabel>{item.method.name}</ModernChartLabel>
                              </ChartRowHeader>
                              <ChartRowBody>
                                <ModernBarContainer>
                                  <ModernBarBackground />
                                  <ModernBar
                                    width={(item.total / totalCaTTC) * 100}
                                    color={theme.colors.celadonblue}
                                  >
                                    <LinearGradient
                                      start={{ x: 0, y: 0 }}
                                      end={{ x: 1, y: 0 }}
                                      colors={['rgba(255, 255, 255, 0.2)', 'transparent']}
                                      style={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        bottom: 0,
                                        borderRadius: normalize(16),
                                      }}
                                    />
                                  </ModernBar>
                                </ModernBarContainer>
                                <ChartRowMetrics>
                                  <MetricValue>{convertToEuros(item.total)}€</MetricValue>
                                  <MetricPercentage>{percentage}%</MetricPercentage>
                                  <MetricCount>{item.quantity} cmd</MetricCount>
                                </ChartRowMetrics>
                              </ChartRowBody>
                            </ModernChartRow>
                          );
                        })}
                      </ChartWrapper>
                    </ModernChartSection>
                  )}

                  <Card.LineBreak height={24} />

                </Card.HelpScrollContainer>
              </MiniCard.Content>
            </MiniCard.Container>
          </MiniCard>
        )}

      </Card.Container>
    </Card>
  );
});

const styles = StyleSheet.create({
  headerGradient: {
    flex: 1,
    height: theme.sizes.listHeaderHeight,
  },
});

// Stats Cards Components
const StatsGrid = styled.View({
  width: '100%',
  flexDirection: 'row',
  gap: normalize(16),
  flexWrap: 'wrap',
});

const StatCard = styled.View({
  flex: 1,
  minWidth: normalize(280),
  borderRadius: normalize(20),
  overflow: 'hidden',
  elevation: 4,
  shadowColor: '#000',
  shadowOffset: { width: 0, height: 4 },
  shadowOpacity: 0.3,
  shadowRadius: 12,
});

const StatIconWrapper = styled.View<{ bgColor: string }>(({ props }) => ({
  width: normalize(56),
  height: normalize(56),
  borderRadius: normalize(16),
  backgroundColor: props.bgColor,
  justifyContent: 'center',
  alignItems: 'center',
}));

const StatIcon = styled.Text({
  fontSize: normalize(28),
});

const StatContent = styled.View({
  flex: 1,
  gap: normalize(4),
});

const StatLabel = styled.Text({
  color: theme.colors.grey,
  fontSize: normalize(13),
  fontWeight: '600',
  textTransform: 'uppercase',
  letterSpacing: 0.5,
});

const StatValue = styled.Text<{ color?: string }>(({ props }) => ({
  color: props.color || theme.colors.babypowder,
  fontSize: normalize(32),
  fontWeight: 'bold',
  lineHeight: normalize(38),
}));

const StatSubtext = styled.Text({
  color: theme.colors.grey,
  fontSize: normalize(12),
  fontWeight: '500',
});

// Modern Chart Components
const ModernChartSection = styled.View({
  width: '100%',
  marginBottom: normalize(32),
});

const SectionHeader = styled.View({
  marginBottom: normalize(20),
  paddingHorizontal: normalize(4),
});

const SectionTitleRow = styled.View({
  flexDirection: 'row',
  alignItems: 'center',
  gap: normalize(12),
  marginBottom: normalize(6),
});

const SectionIcon = styled.Text({
  fontSize: normalize(24),
});

const SectionTitle = styled.Text({
  color: theme.colors.babypowder,
  fontSize: normalize(20),
  fontWeight: 'bold',
  letterSpacing: 0.3,
});

const SectionSubtitle = styled.Text({
  color: theme.colors.grey,
  fontSize: normalize(14),
  fontWeight: '500',
  marginLeft: normalize(36),
});

const ChartWrapper = styled.View({
  gap: normalize(16),
  backgroundColor: 'rgba(253, 255, 252, 0.03)',
  borderRadius: normalize(16),
  padding: normalize(20),
  paddingHorizontal: normalize(24),
  borderWidth: 1,
  borderColor: 'rgba(253, 255, 252, 0.08)',
});

const ModernChartRow = styled.View({
  gap: normalize(10),
});

const ChartRowHeader = styled.View({
  flexDirection: 'row',
  alignItems: 'center',
  gap: normalize(10),
  marginBottom: normalize(8),
});

const ModernChartLabel = styled.Text({
  color: theme.colors.babypowder,
  fontSize: normalize(15),
  fontWeight: '600',
});

const ChartRowBody = styled.View({
  gap: normalize(8),
});

const ModernBarContainer = styled.View({
  height: normalize(32),
  borderRadius: normalize(16),
  overflow: 'hidden',
  position: 'relative',
});

const ModernBarBackground = styled.View({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(253, 255, 252, 0.06)',
  borderRadius: normalize(16),
});

const ModernBar = styled.View<{ width: number; color: string }>(({ props }) => ({
  width: `${Math.max(props.width, 2)}%`,
  height: '100%',
  backgroundColor: props.color,
  borderRadius: normalize(16),
  position: 'relative',
  elevation: 2,
  shadowColor: props.color,
  shadowOffset: { width: 0, height: 2 },
  shadowOpacity: 0.4,
  shadowRadius: 6,
}));

const ChartRowMetrics = styled.View({
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'center',
  paddingHorizontal: normalize(12),
});

const MetricValue = styled.Text({
  color: theme.colors.babypowder,
  fontSize: normalize(16),
  fontWeight: 'bold',
  flex: 1,
});

const MetricPercentage = styled.Text({
  color: theme.colors.gold,
  fontSize: normalize(15),
  fontWeight: 'bold',
  marginRight: normalize(12),
});

const MetricCount = styled.Text({
  color: theme.colors.grey,
  fontSize: normalize(13),
  fontWeight: '500',
});
